import React, { useMemo, useState, useRef } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { bindActionCreators } from 'redux'
import {
    bookmark,
    tags as getTags,
    makeHighlights,
    makeIsSelected,
    makeHighlight,
    makeCreatorRef,
    makeSelectModeEnabled,
    selectModeWorking,
    getCoverSize
} from '~data/selectors/bookmarks'
import * as bookmarksActions from '~data/actions/bookmarks'
import { copyText } from '~modules/browser'
import { openTab } from '~target'

import View from './view'
import Contextmenu from './contextmenu'

const BookmarkItem = React.memo(function BookmarkItem(ownProps) {
    const {
        _id = 0,
        active = false,
        spaceId = 0,
        view = '',
        access = {},
        events = {},
        innerRef = undefined
    } = ownProps

    const dispatch = useDispatch()
    const actions = useMemo(() => bindActionCreators(bookmarksActions, dispatch), [dispatch])

    const getIsSelected = useMemo(() => makeIsSelected(), [])
    const getHighlight = useMemo(() => makeHighlight(), [])
    const getHighlights = useMemo(() => makeHighlights(), [])
    const getCreatorRef = useMemo(() => makeCreatorRef(), [])
    const getSelectModeEnabled = useMemo(() => makeSelectModeEnabled(), [])

    const {
        item,
        tags,
        highlights,
        selected,
        selectModeEnabled,
        selectDisabled,
        highlight,
        creatorRef,
        coverSize
    } = useSelector(
        state => {
            const selectModeEnabled = getSelectModeEnabled(state, spaceId)
            return {
                item: bookmark(state, _id),
                tags: getTags(state, _id),
                highlights: getHighlights(state, _id, 3),
                selected: selectModeEnabled ? getIsSelected(state, spaceId, _id) : false,
                selectModeEnabled,
                selectDisabled: selectModeWorking(state) ? true : false,
                highlight: getHighlight(state, spaceId, _id),
                creatorRef: getCreatorRef(state, _id),
                coverSize: getCoverSize(state, view)
            }
        },
        shallowEqual
    )

    const [menu, setMenu] = useState(false)

    const props = {
        ...ownProps,
        _id,
        active,
        spaceId,
        view,
        access,
        events,
        innerRef,
        actions,
        tags,
        highlights,
        selected,
        selectModeEnabled,
        selectDisabled,
        highlight,
        creatorRef,
        coverSize
    }

    const latest = useRef({ item, props })
    latest.current = { item, props }

    const handlers = useMemo(() => {
        const h = {
            onClick: e => {
                if (!e) return
                const { item, props } = latest.current
                const { selectModeEnabled } = props

                if (e.metaKey || e.ctrlKey || e.nativeEvent.which == 2) {
                    e.preventDefault()
                    return openTab(item.link)
                }

                if (selectModeEnabled || e.shiftKey) {
                    e.preventDefault()
                    return h.onSelectClick(e)
                }

                if (
                    typeof props.events.onBookmarkClick == 'function' &&
                    props.events.onBookmarkClick(item)
                )
                    e.preventDefault()
            },

            onDoubleClick: e => {
                e.preventDefault()
                openTab(latest.current.item.link)
            },

            onSelectClick: (e = {}) => {
                const { item, props } = latest.current
                return props.actions[props.selected && !e.shiftKey ? 'unselectOne' : 'selectOne'](
                    props.spaceId,
                    item._id,
                    e.shiftKey
                )
            },

            onImportantClick: () => {
                const { item, props } = latest.current
                return props.actions.oneImportant(item._id)
            },

            onRemoveClick: () => {
                const { item, props } = latest.current
                return props.actions.oneRemove(item._id)
            },

            onCopyLinkClick: () => copyText(latest.current.item.link),

            onReparseClick: () => {
                const { item, props } = latest.current
                return props.actions.oneReparse(item._id)
            },

            onContextMenu: e => {
                e.preventDefault()
                e.target.focus()
                setMenu(true)
            },

            onContextMenuClose: () => setMenu(false),

            onKeyUp: e => {
                switch (e.keyCode) {
                    case 46: //delete
                    case 8: //backspace
                        e.preventDefault()
                        return h.onRemoveClick()

                    case 13: //enter
                        e.preventDefault()
                        return h.onClick()
                }
            }
        }
        return h
    }, [])

    return (
        <>
            <View
                {...item}
                {...props}
                {...handlers} />

            {menu ? (
                <Contextmenu
                    {...item}
                    {...props}
                    {...handlers} />
            ) : null}
        </>
    )
})

export default BookmarkItem
